import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageContext';
const reveal = {
  initial: {
    opacity: 0,
    y: 24
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
export function Problem() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  // What people no longer want to do
  const oldWays = isFR ? ['Chercher', 'Naviguer', 'Remplir des formulaires', 'Répéter la même information', 'Passer d’un système à l’autre'] : ['Search', 'Navigate', 'Fill forms', 'Repeat information', 'Switch between systems'];
  // What they say instead
  const newWays = isFR ? ['« Inscris-moi. »', '« Paie cette facture. »', '« Renouvelle mon permis. »', '« Prends-moi un rendez-vous. »'] : ['“Register me.”', '“Pay this invoice.”', '“Renew my permit.”', '“Book me an appointment.”'];
  // The three trends, each with a verified stat and the BCITI answer
  const trends = [{
    num: '01',
    title: isFR ? 'La délégation' : 'Delegation',
    desc: isFR ? 'Les usagers ne cherchent plus l’information. Ils délèguent l’action.' : 'People no longer search for information. They delegate the action.',
    statValue: isFR ? '~ la moitié' : '~half',
    statLabel: isFR ? 'préfèrent déjà un assistant IA à un site web pour des tâches comme prendre rendez-vous ou régler un problème.' : 'already prefer an AI assistant over a website for tasks like booking an appointment or resolving an issue.',
    source: 'Adobe, 2025',
    answer: isFR ? 'ATNA agit — avec l’autorisation de l’usager.' : 'ATNA acts — with the user’s authorization.'
  }, {
    num: '02',
    title: isFR ? 'L’hyperpersonnalisation' : 'Hyper-personalization',
    desc: isFR ? 'Ils veulent être compris, pas naviguer un menu générique.' : 'They want to be understood, not navigate a generic menu.',
    statValue: '71% → 34%',
    statLabel: isFR ? 'veulent qu’on anticipe leurs besoins — mais seulement 34 % des organisations y arrivent.' : 'want their needs anticipated — but only 34% of organizations deliver.',
    source: 'Adobe, 2025',
    answer: isFR ? 'L’identité vérifiée rend chaque réponse contextuelle.' : 'Verified identity makes every answer contextual.'
  }, {
    num: '03',
    title: isFR ? 'La confiance' : 'Trust',
    desc: isFR ? 'Avec l’IA partout, ils exigent la fiabilité — et un humain dans le doute.' : 'With AI everywhere, they demand reliability — and a human when in doubt.',
    statValue: '88% → 49%',
    statLabel: isFR ? 'veulent que leurs données soient traitées de façon sûre — mais seulement 49 % des organisations répondent à cette attente.' : 'want their data handled securely — but only 49% of organizations meet that expectation.',
    source: 'Adobe, 2025',
    answer: isFR ? 'ATNA divulgue sa vérification — et passe la main à un expert humain.' : 'ATNA discloses its verification — and hands off to a human expert.'
  }];
  return <section className="bg-white border-t border-[#E4E6EC] py-28 lg:py-36 px-6" data-id="element-545">
      {/* Reframe */}
      <div className="max-w-4xl mx-auto text-center" data-id="element-546">
        <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-547">
          {isFR ? 'Le défi' : 'The challenge'}
        </motion.div>

        <motion.h2 {...reveal} transition={{
        duration: 0.7,
        delay: 0.05
      }} className="font-serif text-4xl md:text-5xl lg:text-[3.25rem] text-[#121212] leading-[1.12] tracking-tight mb-8" data-id="element-548">
          {isFR ? <>
              L’interface a <span className="text-[#4F46E5]" data-id="element-549">changé</span>.
            </> : <>
              The interface has <span className="text-[#4F46E5]" data-id="element-550">changed</span>.
            </>}
        </motion.h2>

        <motion.p {...reveal} transition={{
        duration: 0.7,
        delay: 0.1
      }} className="font-sans text-lg md:text-xl text-[#121212]/60 leading-relaxed max-w-2xl mx-auto" data-id="element-551">
          {isFR ? 'Villes. Universités. Aéroports. Transport. Commerce. Santé. — partout, les attentes des usagers changent plus vite que les organisations ne s’adaptent.' : 'Cities. Universities. Airports. Transit. Retail. Healthcare. — everywhere, user expectations are changing faster than organizations can adapt.'}
        </motion.p>
      </div>

      {/* Old ways vs new ways */}
      <motion.div {...reveal} transition={{
      duration: 0.8,
      delay: 0.15
    }} className="max-w-4xl mx-auto mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E4E6EC] border border-[#E4E6EC]" data-id="element-552">
        {/* No longer want */}
        <div className="bg-[#F7F8FA] p-8 md:p-10" data-id="element-553">
          <div className="font-sans text-xs font-semibold tracking-[0.16em] uppercase text-[#5B6478] mb-6" data-id="element-554">
            {isFR ? 'Ils ne veulent plus' : 'They no longer want to'}
          </div>
          <ul className="space-y-3" data-id="element-555">
            {oldWays.map(w => <li key={w} className="flex items-center gap-3 font-sans text-base text-[#121212]/50" data-id="element-556">
                <span className="text-[#5B6478] shrink-0" data-id="element-557">✕</span>
                <span className="line-through decoration-[#5B6478]/40" data-id="element-558">{w}</span>
              </li>)}
          </ul>
        </div>
        {/* Now they say */}
        <div className="bg-white p-8 md:p-10" data-id="element-559">
          <div className="font-sans text-xs font-semibold tracking-[0.16em] uppercase text-[#4F46E5] mb-6" data-id="element-560">
            {isFR ? 'Ils disent simplement' : 'They simply say'}
          </div>
          <ul className="space-y-3" data-id="element-561">
            {newWays.map(w => <li key={w} className="font-serif text-xl md:text-2xl text-[#121212] leading-snug" data-id="element-562">
                {w}
              </li>)}
          </ul>
        </div>
      </motion.div>

      {/* Punch line */}
      <motion.div {...reveal} transition={{
      duration: 0.7,
      delay: 0.1
    }} className="max-w-3xl mx-auto text-center mt-16" data-id="element-563">
        <p className="font-serif text-2xl md:text-3xl lg:text-[2.25rem] text-[#121212] leading-[1.2]" data-id="element-564">
          {isFR ? 'Les usagers ne veulent plus apprendre votre système.' : 'People no longer want to learn your system.'}
        </p>
        <p className="font-serif text-2xl md:text-3xl lg:text-[2.25rem] text-[#4F46E5] leading-[1.2]" data-id="element-565">
          {isFR ? 'Ils s’attendent à ce que votre système les comprenne.' : 'They expect your system to understand them.'}
        </p>
      </motion.div>

      {/* Three trends */}
      <div className="max-w-6xl mx-auto mt-24" data-id="element-566">
        <motion.div {...reveal} className="text-center font-sans text-xs font-semibold tracking-[0.2em] text-[#5B6478] uppercase mb-12" data-id="element-567">
          {isFR ? 'Trois tendances de fond' : 'Three defining shifts'}
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E6EC] border border-[#E4E6EC]" data-id="element-568">
          {trends.map((tr, i) => <motion.div key={tr.num} {...reveal} transition={{
          duration: 0.6,
          delay: i * 0.1
        }} className="bg-white p-8 md:p-10 flex flex-col" data-id="element-569">
              <div className="font-mono text-sm text-[#4F46E5] mb-5" data-id="element-570">{tr.num}</div>
              <h3 className="font-serif text-2xl text-[#121212] mb-3" data-id="element-571">{tr.title}</h3>
              <p className="font-sans text-sm text-[#5B6478] leading-relaxed mb-8" data-id="element-572">
                {tr.desc}
              </p>
              {/* stat */}
              <div className="mt-auto" data-id="element-573">
                <div className="font-serif text-3xl text-[#121212] mb-2" data-id="element-574">
                  {tr.statValue}
                </div>
                <p className="font-sans text-xs text-[#121212]/55 leading-relaxed mb-2" data-id="element-575">
                  {tr.statLabel}
                </p>
                <div className="font-sans text-[0.65rem] tracking-[0.12em] uppercase text-[#121212]/35 mb-6" data-id="element-576">
                  {tr.source}
                </div>
                {/* BCITI answer */}
                <div className="pt-5 border-t border-[#E4E6EC] flex items-start gap-2" data-id="element-577">
                  <span className="w-1.5 h-1.5 bg-[#4F46E5] mt-1.5 shrink-0" data-id="element-578" />
                  <span className="font-sans text-sm font-medium text-[#121212] leading-snug" data-id="element-579">
                    {tr.answer}
                  </span>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* The switch: more channels vs one foundation */}
      <motion.div {...reveal} transition={{
      duration: 0.7,
      delay: 0.1
    }} className="max-w-4xl mx-auto mt-24 text-center" data-id="element-580">
        <p className="font-sans text-base text-[#5B6478] leading-relaxed mb-2" data-id="element-581">
          {isFR ? 'La plupart des organisations répondent en ajoutant des canaux. Portail. Appli. FAQ. Courriel. SMS.' : 'Most organizations respond by adding channels. Portal. App. FAQ. Email. SMS.'}
        </p>
        <p className="font-serif text-xl md:text-2xl text-[#121212]/70 mb-10" data-id="element-582">
          {isFR ? 'Plus de canaux. Plus de complexité.' : 'More channels. More complexity.'}
        </p>
        <p className="font-sans text-sm font-semibold tracking-[0.16em] uppercase text-[#4F46E5] mb-4" data-id="element-583">
          {isFR ? 'BCITI fait l’inverse' : 'BCITI does the opposite'}
        </p>
        <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug" data-id="element-584">
          {isFR ? 'Une identité vérifiée. Une IA. Chaque point d’entrée. En langage naturel.' : 'One verified identity. One AI. Every touchpoint. In natural language.'}
        </p>
      </motion.div>

      {/* The nail: capacity conclusion */}
      <motion.div {...reveal} transition={{
      duration: 0.8,
      delay: 0.1
    }} className="max-w-4xl mx-auto mt-20 border-t border-[#E4E6EC] pt-16 text-center" data-id="element-585">
        <p className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-[#121212] leading-[1.15]" data-id="element-586">
          {isFR ? 'Répondez à chaque attente' : 'Meet every expectation'}
        </p>
        <p className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-[#4F46E5] leading-[1.15]" data-id="element-587">
          {isFR ? '— et faites plus, sans agrandir votre équipe.' : '— and do more, without growing your team.'}
        </p>
      </motion.div>
    </section>;
}